import React, { useEffect, useRef } from 'react';
import { Form } from 'antd';
import { FileOutlined, LinkOutlined, PaperClipOutlined } from '@ant-design/icons';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { Container } from '@/components/ui/Container';
import TaskAttachmentTile from '@/components/module/projects/task/TaskAttachmentTile';
import { PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { PanelSectionLabel, DetailsAttachmentGridScroll } from '@/pages/inside/tenant/style';
import { resolveAttachmentUrl } from '@hooks/projects/projectHelpers';

const getUrlVariants = (url) => {
    if (!url || typeof url !== 'string') return [];
    const variants = new Set();
    
    const addVariant = (val) => {
        if (!val || typeof val !== 'string') return;
        const trimmed = val.trim();
        if (!trimmed) return;
        variants.add(trimmed);
        try {
            variants.add(decodeURIComponent(trimmed));
        } catch (e) {
            /* ignore */
        }
        try {
            variants.add(encodeURIComponent(trimmed).replace(/%2F/g, '/'));
        } catch (e) {
            /* ignore */
        }
    };

    addVariant(url);
    
    // Check protocol-less vs protocol-added
    if (/^https?:\/\//i.test(url)) {
        const withoutProto = url.replace(/^https?:\/\//i, '');
        addVariant(withoutProto);
    } else {
        addVariant('http://' + url);
        addVariant('https://' + url);
    }
    
    // Resolve via helper
    try {
        const resolved = resolveAttachmentUrl(url);
        if (resolved && resolved !== url) {
            addVariant(resolved);
            if (/^https?:\/\//i.test(resolved)) {
                addVariant(resolved.replace(/^https?:\/\//i, ''));
            }
        }
    } catch (e) {
        /* ignore */
    }

    // Resolve via window.location.origin
    try {
        const parsed = new URL(url, window.location.origin);
        const relative = parsed.pathname + parsed.search + parsed.hash;
        if (relative && relative !== '/') {
            addVariant(relative);
            const cleanRel = relative.startsWith('/') ? relative.substring(1) : relative;
            addVariant(cleanRel);
            addVariant('/' + cleanRel);
        }
    } catch (e) {
        /* ignore */
    }

    if (url.startsWith('/')) {
        addVariant(url.substring(1));
    } else {
        addVariant('/' + url);
    }
    
    return Array.from(variants);
};

const doesHtmlContainFile = (html, file) => {
    if (!html || !file) return false;
    
    const div = document.createElement('div');
    div.innerHTML = html.replace(/&amp;/g, '&');
    
    const anchors = div.querySelectorAll('a');
    const isLinkItem = file.type === 'link';
    
    for (const a of anchors) {
        const href = (a.getAttribute('href') || '').trim();
        
        if (isLinkItem) {
            if (href.startsWith('#attachment')) continue;
            
            const fileUrl = file.rawUrl || file.url || file.href;
            if (fileUrl) {
                const hrefVariants = getUrlVariants(href);
                const fileUrlVariants = getUrlVariants(fileUrl);
                const hasMatch = fileUrlVariants.some(fv => hrefVariants.includes(fv));
                if (hasMatch) return true;
            }
        } else {
            if (href.startsWith('#attachment')) {
                const fullname = (a.getAttribute('data-attachment-fullname') || '').trim();
                const itemTitle = String(file.title || '').trim();
                const itemUrl = String(file.url || file.rawUrl || '').trim();
                const filename = itemUrl.split(/[\\/]/).pop() || '';
                let decodedFilename = filename;
                try {
                    decodedFilename = decodeURIComponent(filename);
                } catch (e) {
                    /* ignore */
                }
                
                if (fullname) {
                    if (itemTitle && fullname === itemTitle) return true;
                    if (filename && (fullname === filename || fullname === decodedFilename)) return true;
                    if (filename && (fullname.endsWith(filename) || filename.endsWith(fullname))) return true;
                }
                
                const textContent = (a.textContent || '').trim();
                if (textContent) {
                    if (itemTitle && textContent === itemTitle) return true;
                    if (filename && (textContent === filename || textContent === decodedFilename)) return true;
                }
            } else {
                const fileUrl = file.rawUrl || file.url || file.href;
                if (fileUrl) {
                    const hrefVariants = getUrlVariants(href);
                    const fileUrlVariants = getUrlVariants(fileUrl);
                    const hasMatch = fileUrlVariants.some(fv => hrefVariants.includes(fv));
                    if (hasMatch) return true;
                }
            }
        }
    }
    
    return false;
};

const removeAnchorFromHtml = (html, targetItem) => {
    if (!html || !targetItem) return html;
    
    const div = document.createElement('div');
    div.innerHTML = html;
    
    const anchors = div.querySelectorAll('a');
    const isLinkItem = targetItem.type === 'link';
    let removed = false;
    
    for (const a of anchors) {
        const href = (a.getAttribute('href') || '').trim();
        
        if (isLinkItem) {
            if (href.startsWith('#attachment')) continue;
            
            const targetUrl = targetItem.rawUrl || targetItem.url || targetItem.href;
            if (targetUrl) {
                const hrefVariants = getUrlVariants(href);
                const targetVariants = getUrlVariants(targetUrl);
                const isMatch = targetVariants.some(tv => hrefVariants.includes(tv));
                if (isMatch) {
                    a.remove();
                    removed = true;
                }
            }
        } else {
            if (href.startsWith('#attachment')) {
                const fullname = (a.getAttribute('data-attachment-fullname') || '').trim();
                const itemTitle = String(targetItem.title || '').trim();
                const itemUrl = String(targetItem.url || targetItem.rawUrl || '').trim();
                const filename = itemUrl.split(/[\\/]/).pop() || '';
                let decodedFilename = filename;
                try {
                    decodedFilename = decodeURIComponent(filename);
                } catch (e) {
                    /* ignore */
                }
                
                let isMatch = false;
                if (fullname) {
                    if (itemTitle && fullname === itemTitle) isMatch = true;
                    else if (filename && (fullname === filename || fullname === decodedFilename)) isMatch = true;
                    else if (filename && (fullname.endsWith(filename) || filename.endsWith(fullname))) isMatch = true;
                }
                
                const textContent = (a.textContent || '').trim();
                if (!isMatch && textContent) {
                    if (itemTitle && textContent === itemTitle) isMatch = true;
                    else if (filename && (textContent === filename || textContent === decodedFilename)) isMatch = true;
                }
                
                if (isMatch) {
                    a.remove();
                    removed = true;
                }
            } else {
                const targetUrl = targetItem.rawUrl || targetItem.url || targetItem.href;
                if (targetUrl) {
                    const hrefVariants = getUrlVariants(href);
                    const targetVariants = getUrlVariants(targetUrl);
                    const isMatch = targetVariants.some(tv => hrefVariants.includes(tv));
                    if (isMatch) {
                        a.remove();
                        removed = true;
                    }
                }
            }
        }
    }
    
    if (removed) {
        // Renumber remaining `#attachment-N` anchors (same as backend does)
        const remaining = [...div.querySelectorAll('a[href^="#attachment"]')];
        remaining.forEach((a, i) => {
            a.setAttribute('href', `#attachment-${i}`);
            if (a.hasAttribute('data-attachment-index')) {
                a.setAttribute('data-attachment-index', String(i));
            }
        });
        return div.innerHTML;
    }
    
    return html;
};

export default function RichTextEditorWithAttachments({
    form,
    fieldName = 'description',
    isEdit = false,
    open = true,
    
    // Mention autocomplete config
    mentionUsers = [],
    mentionMilestones = [],
    mentionSprints = [],
    placeholder = 'Add description — bold, lists, @mentions. Attach files or links.',
    setLiveEditDescription,
    
    // Pending lists
    pendingFiles = [],
    onFilesSelected,
    onRemovePendingFile, // function (id)
    
    pendingLinks = [],
    onRemovePendingLink, // function (id)
    onLinkRequest,
    
    // Existing list
    existingFiles = [],
    existingLinks = [],
    onRemoveExistingAttachment, // function (item)
}) {
    const descriptionValue = Form.useWatch(fieldName, form);
    const previousHtmlRef = useRef('');

    // Keep previousHtmlRef in sync when open changes
    useEffect(() => {
        if (!open) {
            previousHtmlRef.current = '';
        } else {
            previousHtmlRef.current = descriptionValue || '';
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    
    // Auto-remove badges when user erases text/links from editor
    useEffect(() => {
        if (!open) return;
        const html = descriptionValue || '';
        const prevHtml = previousHtmlRef.current || '';
        
        // 1. Sync pending files
        const files = pendingFiles || [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const token = `#attachment-${i}`;
            const wasPresent = prevHtml.includes(token);
            const isPresent = html.includes(token);
            if (wasPresent && !isPresent) {
                if (onRemovePendingFile) {
                    onRemovePendingFile(file.id);
                }
            }
        }
        
        // 2. Sync pending links
        const links = pendingLinks || [];
        for (const link of links) {
            const wasPresent = doesHtmlContainFile(prevHtml, { type: 'link', url: link.url });
            const isPresent = doesHtmlContainFile(html, { type: 'link', url: link.url });
            if (wasPresent && !isPresent) {
                if (onRemovePendingLink) {
                    onRemovePendingLink(link.id);
                }
            }
        }
        
        // 3. Sync existing files and links
        if (isEdit) {
            const exFiles = existingFiles || [];
            for (const file of exFiles) {
                const wasPresent = doesHtmlContainFile(prevHtml, file);
                const isPresent = doesHtmlContainFile(html, file);
                if (wasPresent && !isPresent) {
                    if (onRemoveExistingAttachment) {
                        onRemoveExistingAttachment(file);
                    }
                }
            }
            
            const exLinks = existingLinks || [];
            for (const link of exLinks) {
                const wasPresent = doesHtmlContainFile(prevHtml, link);
                const isPresent = doesHtmlContainFile(html, link);
                if (wasPresent && !isPresent) {
                    if (onRemoveExistingAttachment) {
                        onRemoveExistingAttachment(link);
                    }
                }
            }
        }

        previousHtmlRef.current = html;
    }, [
        descriptionValue,
        open,
        isEdit,
        pendingFiles,
        pendingLinks,
        existingFiles,
        existingLinks,
        onRemovePendingFile,
        onRemovePendingLink,
        onRemoveExistingAttachment
    ]);

    // Handle user clicking the delete icon/badge below editor
    const handleRemoveFileBadge = (file, isPending) => {
        if (isPending) {
            if (onRemovePendingFile) onRemovePendingFile(file.id);
        } else {
            // Remove from editor HTML
            let content = form.getFieldValue(fieldName) || '';
            const newContent = removeAnchorFromHtml(content, file);
            if (newContent !== content) {
                form.setFieldValue(fieldName, newContent);
                if (typeof setLiveEditDescription === 'function') setLiveEditDescription(newContent);
                form.validateFields([fieldName]);
            }
            if (onRemoveExistingAttachment) onRemoveExistingAttachment(file);
        }
    };

    const handleRemoveLinkBadge = (id) => {
        let isPending = true;
        let foundItem = null;
        
        const foundPending = pendingLinks.find((l) => l.id === id);
        if (foundPending) {
            isPending = true;
            foundItem = { type: 'link', url: foundPending.url };
        } else {
            const foundExisting = existingLinks.find((l) => l.id === id);
            if (foundExisting) {
                isPending = false;
                foundItem = foundExisting;
            }
        }
        
        if (foundItem) {
            let content = form.getFieldValue(fieldName) || '';
            const newContent = removeAnchorFromHtml(content, foundItem);
            if (newContent !== content) {
                form.setFieldValue(fieldName, newContent);
                if (typeof setLiveEditDescription === 'function') setLiveEditDescription(newContent);
                form.validateFields([fieldName]);
            }
        }
        
        if (isPending) {
            if (onRemovePendingLink) onRemovePendingLink(id);
        } else {
            if (onRemoveExistingAttachment && foundItem) onRemoveExistingAttachment(foundItem);
        }
    };

    const showAttachmentsContainer = 
        pendingFiles.length > 0 || 
        pendingLinks.length > 0 || 
        (isEdit && (existingFiles.length > 0 || existingLinks.length > 0));

    return (
        <>
            <Form.Item
                name={fieldName}
                style={{ marginBottom: 0 }}
            >
                <RichTextEditor
                    submissionNeutralLight
                    isAttachment
                    isLink
                    isMention
                    placeholder={placeholder}
                    showAttachButton
                    mentionUsers={mentionUsers}
                    mentionMilestones={mentionMilestones}
                    mentionSprints={mentionSprints}
                    onFilesSelected={onFilesSelected}
                    onLinkRequest={onLinkRequest}
                    attachmentIndexOffset={pendingFiles.length}
                    onAttachmentRemoved={(index) => {
                        const p = pendingFiles[index];
                        if (p && onRemovePendingFile) {
                            onRemovePendingFile(p.id);
                        }
                    }}
                />
            </Form.Item>

            {showAttachmentsContainer && (
                <Container
                    $marginTop="8px"
                    $marginBottom="16px"
                    $paddingTop="12px"
                    $borderTop="1px solid var(--ant-color-border, #f0f0f0)"
                    $display="flex"
                    $flexDirection="column"
                    style={{ gap: 16 }}
                >
                    {/* Render pending and existing links in a single section */}
                    {(pendingLinks.length > 0 || (isEdit && existingLinks.length > 0)) && (
                        <div>
                            <PanelSectionLabel
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    marginBottom: 8,
                                    fontSize: 11,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    opacity: 0.75,
                                }}
                            >
                                <LinkOutlined />
                                Links
                            </PanelSectionLabel>
                            <PendingLinkWrap
                                links={[
                                    ...(isEdit && existingLinks
                                        ? existingLinks.map((link) => ({
                                              id: link.id,
                                              url: link.href || link.url,
                                              title: link.title || link.href || link.url,
                                          }))
                                        : []),
                                    ...pendingLinks.map((link) => ({
                                        id: link.id,
                                        url: link.url,
                                        title: link.title || link.url,
                                    })),
                                ]}
                                onRemoveLink={handleRemoveLinkBadge}
                            />
                        </div>
                    )}

                    {/* Render pending and existing file attachments in a single grid */}
                    {(pendingFiles.length > 0 || (isEdit && existingFiles.length > 0)) && (
                        <div>
                            <PanelSectionLabel
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    marginBottom: 8,
                                    fontSize: 11,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    opacity: 0.75,
                                }}
                            >
                                <PaperClipOutlined />
                                Attachments
                            </PanelSectionLabel>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    gap: 10,
                                    marginTop: 8,
                                    marginBottom: 16,
                                }}
                            >
                                {pendingFiles.map((p) => (
                                    <TaskAttachmentTile
                                        key={p.id}
                                        href={p.preview}
                                        title={p.name}
                                        isImage={(p.type || '').startsWith('image/')}
                                        ext={p.name.split('.').pop()}
                                        onOpenFile={p.preview ? (url) => window.open(url, '_blank') : undefined}
                                        onRemove={() => handleRemoveFileBadge(p, true)}
                                        style={{ width: '100%', height: '110px' }}
                                    />
                                ))}
                                {isEdit && existingFiles.map((f) => (
                                    <TaskAttachmentTile
                                        key={f.id}
                                        href={f.url}
                                        title={f.title || 'File'}
                                        isImage={f.isImage}
                                        ext={f.ext}
                                        onOpenFile={(url) => window.open(url, '_blank')}
                                        onRemove={() => handleRemoveFileBadge(f, false)}
                                        style={{ width: '100%', height: '110px' }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </Container>
            )}
        </>
    );
}
